declare const __DEV__: boolean;
declare const __USE_LOCAL_QUALITY_FORMS__: boolean;

import Domain from "enums/Domain";
import { azForHost } from "util/envUtils";

/**
 * Domain-based deployment environment definitions.
 * Keys from this object are matched against the end of
 * the domain when config is generated, so 'foo.bar.com'
 * will match 'bar.com'
 *
 * only overrides to defaultConfig.js need to be specified here
 *
 * URI definitions are handled via separate object properties
 * for the relevant parts of the URI, such that
 * 'https://public-api.us-east-1.inindca.com:8000/api/v1'
 * would be represented as:
 *  {
 *      protocol: 'https',
 *      hostname: 'public-api.us-east-1.inindca.com',
 *      port: '8000',
 *      path: '/api/v1',
 *  }
 *
 * The 'port' and 'path' properties are both optional
 */

interface ServiceDefinition {
    protocol: string;
    hostname: string;
    path?: string;
    maxReconnectInterval?: number;
    timeout?: number;
}

export interface Environment {
    envName: "dca" | "tca" | "pca";
    embeddingOrigin?: string;
    localLogLevel?: string;
    serverLogLevel?: string;
    api: ServiceDefinition;
    journey: ServiceDefinition;
    journey_micro: ServiceDefinition;
    qualityForms: ServiceDefinition;
    giaTranscript: ServiceDefinition;
    giaTranscriptV2: ServiceDefinition;
}

const buildHostname = (subdomain: string, domain: Domain): string =>
    subdomain ? `${subdomain}.${domain}` : domain;

const isAppSubdomain = window.location.hostname.startsWith("app.");
const isAppRegionalSubdomain =
    window.location.hostname.startsWith("app-regional");
const getAppSubdomain = () => {
    if (isAppSubdomain) {
        return "app";
    }
    if (isAppRegionalSubdomain) {
        return "app-regional";
    }
    return "apps";
};
const DCA_APP_HOSTNAME = buildHostname(getAppSubdomain(), Domain.DCA);
const TCA_APP_HOSTNAME = buildHostname(getAppSubdomain(), Domain.TCA);

const prodEnv = (domain: Domain): Environment => {
    const appHostname = buildHostname(getAppSubdomain(), domain);
    return {
        envName: "pca",
        localLogLevel: "info",
        api: {
            protocol: "https",
            hostname: buildHostname("api", domain),
            path: "/api/v2",
        },
        journey: {
            protocol: "https",
            hostname: appHostname,
            path: "/journey",
        },
        journey_micro: {
            protocol: "https",
            hostname: appHostname,
            path: "/journey-micro",
        },
        giaTranscript: {
            protocol: "https",
            hostname: appHostname,
            path: "/transcript-ui",
        },
        giaTranscriptV2: {
            protocol: "https",
            hostname: appHostname,
            path: "/transcript-ui-v2",
        },
        qualityForms: {
            protocol: "https",
            hostname: appHostname,
            path: "/quality-forms",
        },
    };
};

const ENVIRONMENTS: Partial<Record<Domain, Environment>> = {
    [Domain.DCA]: {
        envName: "dca",
        localLogLevel: "debug",
        api: {
            protocol: "https",
            hostname: buildHostname("api", Domain.DCA),
            path: "/api/v2",
        },
        journey: {
            protocol: "https",
            hostname: DCA_APP_HOSTNAME,
            path: "/journey",
        },
        journey_micro: {
            protocol: "https",
            hostname: DCA_APP_HOSTNAME,
            path: "/journey-micro",
        },
        giaTranscript: {
            protocol: "https",
            hostname: DCA_APP_HOSTNAME,
            path: "/transcript-ui",
        },
        giaTranscriptV2: {
            protocol: "https",
            hostname: DCA_APP_HOSTNAME,
            path: "/transcript-ui-v2",
        },
        qualityForms: {
            protocol: "https",
            hostname: DCA_APP_HOSTNAME,
            path: "/quality-forms",
        },
    },
    [Domain.TCA]: {
        envName: "tca",
        localLogLevel: "debug",
        api: {
            protocol: "https",
            hostname: buildHostname("api", Domain.TCA),
            path: "/api/v2",
        },
        journey: {
            protocol: "https",
            hostname: TCA_APP_HOSTNAME,
            path: "/journey",
        },
        journey_micro: {
            protocol: "https",
            hostname: TCA_APP_HOSTNAME,
            path: "/journey-micro",
        },
        giaTranscript: {
            protocol: "https",
            hostname: TCA_APP_HOSTNAME,
            path: "/transcript-ui",
        },
        giaTranscriptV2: {
            protocol: "https",
            hostname: TCA_APP_HOSTNAME,
            path: "/transcript-ui-v2",
        },
        qualityForms: {
            protocol: "https",
            hostname: TCA_APP_HOSTNAME,
            path: "/quality-forms",
        },
    },

    [Domain.COM]: prodEnv(Domain.COM),
    [Domain.AU]: prodEnv(Domain.AU),
    [Domain.IE]: prodEnv(Domain.IE),
    [Domain.JP]: prodEnv(Domain.JP),
    [Domain.DE]: prodEnv(Domain.DE),
    [Domain.USW2]: prodEnv(Domain.USW2),
    [Domain.APNE2]: prodEnv(Domain.APNE2),
    [Domain.CAC1]: prodEnv(Domain.CAC1),
    [Domain.EUW2]: prodEnv(Domain.EUW2),
    [Domain.APS1]: prodEnv(Domain.APS1),
    [Domain.SAE1]: prodEnv(Domain.SAE1),
    [Domain.APNE3]: prodEnv(Domain.APNE3),
    [Domain.MEC1]: prodEnv(Domain.MEC1),
    [Domain.EUC2]: prodEnv(Domain.EUC2),
    [Domain.APSE1]: prodEnv(Domain.APSE1),
    [Domain.MXC1]: prodEnv(Domain.MXC1),
    [Domain.GOVUSE2]: prodEnv(Domain.GOVUSE2),
    [Domain.MAXIMUS]: prodEnv(Domain.MAXIMUS),
};

if (__DEV__) {
    ENVIRONMENTS[Domain.WILDCARD] = {
        envName: "dca",
        embeddingOrigin: "*",
        localLogLevel: "debug",
        serverLogLevel: "none",
        api: {
            hostname: buildHostname("api", Domain.DCA),
            path: "/api/v2",
            protocol: "https",
        },
        journey: {
            protocol: "https",
            hostname: DCA_APP_HOSTNAME,
            path: "/journey",
        },
        journey_micro: {
            protocol: "https",
            hostname: DCA_APP_HOSTNAME,
            path: "/journey-micro",
        },
        giaTranscript: {
            protocol: "https",
            hostname: DCA_APP_HOSTNAME,
            path: "/transcript-ui",
        },
        giaTranscriptV2: {
            protocol: "https",
            hostname: DCA_APP_HOSTNAME,
            path: "/transcript-ui-v2",
        },
        qualityForms: {
            protocol: "https",
            hostname: __USE_LOCAL_QUALITY_FORMS__
                ? "localhost:8002"
                : DCA_APP_HOSTNAME,
            path: __USE_LOCAL_QUALITY_FORMS__ ? undefined : "/quality-forms",
        },
    };
}

export default ENVIRONMENTS;
